import { Box, Portal } from '@chakra-ui/react';
import { ContextMenuFactory } from '@components/map/contextMenu/ContextMenuFactory';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import { useMapLayerMouseEvent } from '@hooks/useMapLayerMouseEvent';
import { GeoJSONFeature, MapEventOf, MapMouseEvent } from 'mapbox-gl';
import { useCallback, useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { SourceNameEnum } from '@/types/map.types';

function createVirtualElement(x: number, y: number) {
  return {
    getBoundingClientRect: () => new DOMRect(x, y, 0, 0),
    contextElement: undefined,
  };
}

export function ContextMenu() {
  const { current: mapbox } = useMap();

  const [menuEvent, setMenuEvent] = useState<MapMouseEvent | null>(null);
  const [selectedZoneFeature, setSelectedZoneFeature] = useState<GeoJSONFeature | null>(null);
  const [open, setOpen] = useState(false);

  const floating = useFloating({
    placement: 'right-start',
    middleware: [offset(6), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const handleContextMenu = useCallback(
    (event: MapEventOf<'contextmenu'>) => {
      if (!mapbox) return;

      setMenuEvent(event);
      setOpen(true);

      const x = event.originalEvent.pageX;
      const y = event.originalEvent.pageY;

      const virtualRef = createVirtualElement(x, y);

      floating.refs.setReference(virtualRef);
    },
    [mapbox, floating.refs],
  );

  useMapLayerMouseEvent('contextmenu', handleContextMenu);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const el = floating.refs.floating.current;

      if (el && !el.contains(event.target as Node)) {
        setOpen(false);
        setMenuEvent(null);
        if (selectedZoneFeature) {
          mapbox?.getMap().setFeatureState(
            { source: SourceNameEnum.ZONES, id: selectedZoneFeature.id! },
            {
              isSelected: false,
            },
          );
          setSelectedZoneFeature(null);
        }
      }
    },
    [floating.refs.floating, mapbox, selectedZoneFeature],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  if (!open || !menuEvent) return null;

  return (
    <Portal>
      <Box
        ref={floating.refs.setFloating}
        style={{
          ...floating.floatingStyles,
          zIndex: 4,
          position: floating.strategy,
        }}
      >
        <ContextMenuFactory
          event={menuEvent}
          clickPosition={{
            x: menuEvent.originalEvent.pageX,
            y: menuEvent.originalEvent.pageY,
          }}
          onClose={() => setOpen(false)}
          selectedZoneFeature={selectedZoneFeature}
          setSelectedZoneFeature={setSelectedZoneFeature}
        />
      </Box>
    </Portal>
  );
}
